import { useState, useEffect } from 'react';
import { Building2, Edit, Calendar, FileText, Shield, Cloud } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useSettingsStore } from '../stores/settingsStore';
import { USER_TYPES } from '../utils/constants';
import { tenantService } from '../services';
import type { Tenant } from '../services';
import StatusBadge from '../components/common/StatusBadge';
import { formatDate } from '../utils/formatDate';
import { useToast } from '../components/common/ToastContainer';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';

interface ExtendedTenant extends Tenant {
  appUsers?: number;
  devices?: number;
  admins?: number;
  edges?: number;
  mtges?: number;
  connectors?: number;
}

const MyTenant = () => {
  const { user } = useAuthStore();
  const { getDateFormatSettings } = useSettingsStore();
  const toast = useToast();
  const { canUpdate } = usePermissions();
  const [tenant, setTenant] = useState<ExtendedTenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [gvsaStatus, setGvsaStatus] = useState<any>(null);

  useEffect(() => {
    const fetchTenant = async () => {
      if (!user?.tenantId) return;
      try {
        setLoading(true);
        const [data, statusData] = await Promise.all([
          tenantService.getById(user.tenantId),
          tenantService.getGlobalVsaStatus(user.tenantId).catch(() => ({ registered: false, status: 'not_registered' })),
        ]);
        setTenant(data);
        setGvsaStatus(statusData);
        setEditForm({ name: data.name || '', description: data.description || '' });
      } catch (err: any) {
        toast.error('Load Failed', err.message || 'Failed to load tenant details');
      } finally {
        setLoading(false);
      }
    };
    fetchTenant();
  }, [user?.tenantId]);

  // SuperAdmins use Administration > Tenants instead
  if (user?.type === USER_TYPES.SUPER_ADMIN) {
    return <Navigate to="/admin/tenants" replace />;
  }

  const handleSave = async () => {
    if (!user?.tenantId || !tenant) return;
    try {
      setSaving(true);
      await tenantService.update(user.tenantId, {
        name: editForm.name,
        description: editForm.description,
      });
      const updated = await tenantService.getById(user.tenantId);
      setTenant(updated);
      setIsEditing(false);
      toast.success('Tenant Updated', 'Tenant details updated successfully');
    } catch (err: any) {
      toast.error('Update Failed', err.message || 'Failed to update tenant');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading tenant details...</div>;
  }

  if (!tenant) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No tenant information available.
      </div>
    );
  }

  const dateSettings = getDateFormatSettings();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Tenant</h1>
          <p className="text-gray-600 mt-1">View and manage your tenant details</p>
        </div>
        {!isEditing && canUpdate && (
          <button
            onClick={() => setIsEditing(true)}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Edit className="w-4 h-4" />
            <span>Edit</span>
          </button>
        )}
      </div>

      {/* Tenant Info Card */}
      <div className="card">
        <div className="p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-primary-600" />
            </div>
            <div>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="input-base text-xl font-bold"
                  placeholder="Tenant name"
                />
              ) : (
                <h2 className="text-xl font-bold text-gray-900">{tenant.name}</h2>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoRow
              icon={Calendar}
              label="Created"
              value={tenant.createTime ? formatDate(tenant.createTime, dateSettings) : 'Unknown'}
            />
            <InfoRow icon={Shield} label="Status" value="Active" />

            {/* Global VSA Status */}
            <div className="flex items-center space-x-3">
              <Cloud className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-500">Global VSA</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {gvsaStatus?.registered ? (
                    <>
                      <StatusBadge
                        status={gvsaStatus.status === 'active' ? 'active' :
                          gvsaStatus.status === 'pending_approval' ? 'pending' :
                            gvsaStatus.status === 'suspended' ? 'inactive' : 'error'}
                      />
                      <span className="text-sm text-gray-600">{gvsaStatus.message || gvsaStatus.status}</span>
                    </>
                  ) : (
                    <>
                      <StatusBadge status="inactive" />
                      <span className="text-sm text-gray-600">Not registered</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500">Description</p>
                  {isEditing ? (
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="input-base w-full mt-1"
                      rows={3}
                      placeholder="Tenant description"
                    />
                  ) : (
                    <p className="text-sm text-gray-900 mt-1">
                      {tenant.description || 'No description'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Edit Actions */}
          {isEditing && (
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditForm({ name: tenant.name || '', description: tenant.description || '' });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              {canUpdate && (
              <button
                onClick={handleSave}
                disabled={saving || !editForm.name.trim()}
                className="btn-primary disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tenant.edges !== undefined && (
          <MiniStat label="Edges" value={tenant.edges} />
        )}
        {tenant.mtges !== undefined && (
          <MiniStat label="Shared Gateways" value={tenant.mtges} />
        )}
        {tenant.connectors !== undefined && (
          <MiniStat label="Connectors" value={tenant.connectors} />
        )}
        {tenant.devices !== undefined && (
          <MiniStat label="Devices" value={tenant.devices} />
        )}
        {tenant.appUsers !== undefined && (
          <MiniStat label="VPN Users" value={tenant.appUsers} />
        )}
        {tenant.admins !== undefined && (
          <MiniStat label="Admins" value={tenant.admins} />
        )}
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }: { icon: typeof Calendar; label: string; value: string }) => (
  <div className="flex items-center space-x-3">
    <Icon className="w-5 h-5 text-gray-400" />
    <div>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="text-sm text-gray-900">{value}</p>
    </div>
  </div>
);

const MiniStat = ({ label, value }: { label: string; value: number }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500 mt-1">{label}</p>
  </div>
);

export default MyTenant;
